import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Schema } from "@bw/shared/types/generated";
import { Fragment, useRef, useState } from "react";
import { MatchCard, type MatchCardSide } from "../../../common/components/MatchCard";
import { visuallyHidden } from "../../../common/visuallyHidden";

type Snapshot = Schema["TournamentParticipantLiveSnapshot"];
type PublicMatch = Schema["TournamentPublicMatch"];
type SchedulePage =
  | Schema["TournamentParticipantSchedulePage"]
  | Schema["TournamentPublicSchedulePage"];
export type TournamentPublicScheduleState = {
  pages: SchedulePage[];
  loading: boolean;
  error: boolean;
  hasNextPage: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
};

const stateLabel = (value: string) =>
  value === "expected" ? "Not checked in" : value.replaceAll("_", " ");

const titleLabel = (value: string) => {
  const label = stateLabel(value);
  return `${label.slice(0, 1).toUpperCase()}${label.slice(1)}`;
};

const opponentLabel = (match: Snapshot["next_matches"][number]) =>
  match.opponents.length > 0
    ? match.opponents
        .map((opponent) => opponent.display_name ?? opponent.handle)
        .join(" / ")
    : match.outcome === "bye"
      ? "Bye"
      : "Opponent";

const publicParticipantLabel = (match: PublicMatch) =>
  match.participants.length > 0
    ? match.participants
        .map((participant) => participant.display_name ?? participant.handle)
        .join(" / ")
    : "Awaiting qualifier";

const publicScoreLabel = (match: PublicMatch) =>
  (match.score ?? []).map((game) => `${game.side1}–${game.side2}`).join(", ");

const publicScheduledTime = (match: PublicMatch) => {
  if (match.schedule_status !== "published" || !match.scheduled_start_at) {
    return null;
  }
  return new Date(match.scheduled_start_at).toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: match.venue_timezone ?? undefined,
  });
};

const publicScheduleDay = (match: PublicMatch) => {
  if (match.schedule_status !== "published" || !match.scheduled_start_at) {
    return "Time to be announced";
  }
  try {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "full",
      timeZone: match.venue_timezone ?? undefined,
    }).format(new Date(match.scheduled_start_at));
  } catch {
    return "Published schedule";
  }
};

const timeOnly = (iso: string, timeZone?: string | null) => {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone ?? undefined,
    });
  } catch {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
};

const handleLabel = (handle: string) => `#${handle.replace(/^#/, "")}`;

/** One public match as the approved match card, each player "Name #handle". */
function PublicMatchCard({ match }: { match: PublicMatch }) {
  const live = ["in_progress", "interval", "suspended"].includes(match.state);
  const score = match.score ?? [];
  const finished = ["completed", "finished"].includes(match.state);
  const scheduledTime = publicScheduledTime(match);
  const side = (key: "side1" | "side2"): MatchCardSide => ({
    players: match.participants
      .filter((participant) => participant.side === key)
      .map((participant) => ({
        name: participant.display_name ?? handleLabel(participant.handle),
        handle: participant.display_name ? handleLabel(participant.handle) : null,
      })),
    placeholder: match.participants.length === 0 ? "Awaiting qualifier" : "TBD",
    scores: score.map((game) => (key === "side1" ? game.side1 : game.side2)),
  });
  const status =
    match.timing_status === "delayed"
      ? "Delayed"
      : live
        ? null
        : finished
          ? "Final"
          : match.state === "pending"
            ? scheduledTime && match.scheduled_start_at
              ? `Next ~${timeOnly(match.scheduled_start_at, match.venue_timezone)}`
              : "Upcoming"
            : titleLabel(match.state);
  const parts = [match.court_label ?? null, status, match.match_label].filter(
    (part): part is string => Boolean(part),
  );
  return (
    <MatchCard
      live={live}
      ariaLabel={match.match_label}
      header={parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? " · " : null}
          <span>{part}</span>
        </Fragment>
      ))}
      sides={[side("side1"), side("side2")]}
    />
  );
}

function CourtMatchLine({
  label,
  match,
}: {
  label: "Now" | "Next" | "Finished";
  match: PublicMatch;
}) {
  const score = publicScoreLabel(match);
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">
        {label}: {match.match_label}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {publicParticipantLabel(match)}
      </Typography>
      {match.timing_status === "delayed" ? (
        <Typography color="warning.main" variant="body2">
          Delayed
        </Typography>
      ) : null}
      {score ? (
        <Typography color="text.secondary" variant="body2">
          Score: {score}
        </Typography>
      ) : null}
    </Stack>
  );
}

export function TournamentPublicLiveBoard({
  courts,
  initialMatches = [],
  schedule,
}: {
  courts: Schema["TournamentPublicCourt"][];
  initialMatches?: PublicMatch[];
  schedule?: TournamentPublicScheduleState;
}) {
  const loadedScheduleItems =
    schedule?.pages.flatMap((page) => page.items) ?? [];
  const scheduleItems =
    loadedScheduleItems.length > 0 ? loadedScheduleItems : initialMatches;
  const scheduleGroups = scheduleItems.reduce<
    Array<{ day: string; matches: PublicMatch[] }>
  >((groups, match) => {
    const day = publicScheduleDay(match);
    const current = groups.at(-1);
    if (current?.day === day) current.matches.push(match);
    else groups.push({ day, matches: [match] });
    return groups;
  }, []);

  return (
    <>
      {courts.length > 0 ? (
        <Stack component="section" aria-label="Court board" spacing={1}>
          <Typography variant="subtitle1">Court board</Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {courts.map((court) => (
              <Paper key={court.court_id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.75}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {court.label}
                  </Typography>
                  {court.current_match ? (
                    <CourtMatchLine label="Now" match={court.current_match} />
                  ) : null}
                  {court.upcoming_matches?.map((match, index) => (
                    <CourtMatchLine
                      key={`${match.match.kind}:${match.match.match_id}:${index}`}
                      label="Next"
                      match={match}
                    />
                  ))}
                  {court.recent_matches?.map((match, index) => (
                    <CourtMatchLine
                      key={`${match.match.kind}:${match.match.match_id}:${index}`}
                      label="Finished"
                      match={match}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Box>
        </Stack>
      ) : null}
      <Stack component="section" aria-label="Tournament schedule" spacing={1}>
        <Typography component="h3" sx={visuallyHidden}>
          Tournament schedule
        </Typography>
        {schedule?.loading && scheduleItems.length === 0 ? (
          <Typography role="status">Loading schedule…</Typography>
        ) : null}
        {schedule?.error ? (
          <Alert
            severity="error"
            action={
              schedule.onRetry ? (
                <Button size="small" onClick={schedule.onRetry}>
                  Retry schedule
                </Button>
              ) : undefined
            }
          >
            The tournament schedule is temporarily unavailable.
          </Alert>
        ) : null}
        {!schedule?.loading &&
        !schedule?.error &&
        scheduleItems.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            Schedule awaiting publication.
          </Typography>
        ) : null}
        {scheduleGroups.map((group) => (
          <Stack component="section" key={group.day} spacing={1}>
            <Typography component="h4" sx={visuallyHidden}>
              {group.day}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 1,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  lg: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              {group.matches.map((match, index) => (
                <PublicMatchCard
                  key={`${match.match.kind}:${match.match.match_id}:${index}`}
                  match={match}
                />
              ))}
            </Box>
          </Stack>
        ))}
        {schedule?.hasNextPage && schedule.onLoadMore ? (
          <Button
            size="small"
            disabled={schedule.loadingMore}
            onClick={schedule.onLoadMore}
          >
            Load more schedule
          </Button>
        ) : null}
      </Stack>
    </>
  );
}

export function TournamentLiveTab({
  snapshot,
  onCheckIn,
  loading,
  authorityError = false,
  errorStatus,
  onRetry,
  schedule,
}: {
  snapshot: Snapshot | null;
  onCheckIn: () => Promise<void>;
  loading: boolean;
  authorityError?: boolean;
  errorStatus?: number;
  onRetry?: () => Promise<unknown>;
  schedule?: TournamentPublicScheduleState;
}) {
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const checkIn = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    try {
      await onCheckIn();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Check-in failed");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  if (authorityError) {
    const denied = errorStatus === 403 || errorStatus === 404;
    return (
      <Stack component="section" aria-label="Tournament live">
        <Alert
          severity={denied ? "info" : "error"}
          action={
            !denied && onRetry ? (
              <Button size="small" onClick={() => void onRetry()}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {denied
            ? "Participant check-in and personal match details are available only to registered participants."
            : "Schedule and results are temporarily unavailable."}
        </Alert>
      </Stack>
    );
  }
  if (loading || !snapshot) {
    return (
      <Stack component="section" aria-label="Tournament live">
        <Typography role="status">Loading schedule and results…</Typography>
      </Stack>
    );
  }
  const schedulePlanRevision = snapshot.next_matches.find(
    (match) =>
      match.schedule_plan_revision !== null &&
      match.schedule_plan_revision !== undefined,
  )?.schedule_plan_revision;
  const venueTimezone = snapshot.next_matches.find((match) =>
    Boolean(match.venue_timezone),
  )?.venue_timezone;
  const recentResults = snapshot.recent_results ?? [];
  const courtBoard = snapshot.court_board ?? [];
  // The schedule names each match ("Open Doubles · Semifinal"); the pinned
  // card borrows that label by match id rather than inventing one.
  const matchLabels = new Map(
    (schedule?.pages ?? [])
      .flatMap((page) => page.items)
      .map((item) => [item.match.match_id, item.match_label] as const),
  );

  return (
    <Stack component="section" aria-label="Tournament live" spacing={2}>
      <Typography component="h2" sx={visuallyHidden}>
        Your schedule
      </Typography>
      {/* Presence, plan revision and venue zone are announced, not shown. */}
      <Typography role="status" sx={visuallyHidden}>
        {pending ? "Checking in…" : stateLabel(snapshot.own_presence.state)}
      </Typography>
      {schedulePlanRevision !== undefined ? (
        <Typography sx={visuallyHidden}>
          Plan revision {schedulePlanRevision}
        </Typography>
      ) : null}
      {venueTimezone ? (
        <Typography sx={visuallyHidden}>Venue time: {venueTimezone}</Typography>
      ) : null}
      {error ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button size="small" onClick={() => void checkIn()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}
      {snapshot.own_presence.allowed_actions?.includes("self_check_in") ? (
        <Button
          size="small"
          variant="contained"
          disabled={pending}
          onClick={() => void checkIn()}
        >
          Check in
        </Button>
      ) : null}
      {snapshot.next_matches.map((match, index) => {
        const score = match.score ?? [];
        const published =
          match.schedule_status === "published" && match.scheduled_start_at
            ? match.scheduled_start_at
            : null;
        const estimated =
          !published &&
          match.schedule_status === "estimated" &&
          match.estimated_start_at
            ? match.estimated_start_at
            : null;
        const startAt = published ?? estimated;
        const title = matchLabels.get(match.match.match_id);
        return (
          <Paper
            aria-label={`Live match ${index + 1}`}
            component="section"
            key={`${match.state}:${index}`}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: "12px",
              ...(index === 0
                ? { borderColor: "primary.main", borderWidth: 1.5 }
                : {}),
            }}
          >
            <Stack spacing={0.25}>
              {index === 0 ? (
                <Typography
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}
                >
                  Your next match
                </Typography>
              ) : null}
              {title ? (
                <Typography noWrap sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
              ) : null}
              {match.opponents.length > 0 ? (
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    vs
                  </Typography>
                  {match.opponents.map((opponent, opponentIndex) => (
                    <Typography
                      key={opponentIndex}
                      noWrap
                      sx={{ fontWeight: 700 }}
                    >
                      {opponent.display_name ?? handleLabel(opponent.handle)}
                      {opponent.display_name ? (
                        <Box
                          component="span"
                          sx={{ color: "text.secondary", fontWeight: 400 }}
                        >
                          {` ${handleLabel(opponent.handle)}`}
                        </Box>
                      ) : null}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 700 }}>
                  {opponentLabel(match)}
                </Typography>
              )}
              {/* One line: court and the start ("~" while estimated). */}
              {match.court_label || startAt ? (
                <Typography color="text.secondary" variant="body2">
                  {match.court_label ? <span>{match.court_label}</span> : null}
                  {match.court_label && startAt ? " · " : null}
                  {startAt ? (
                    <time
                      aria-label={published ? "Published start" : "Estimated start"}
                      dateTime={startAt}
                    >
                      {estimated ? "~" : ""}
                      {timeOnly(startAt, match.venue_timezone)}
                    </time>
                  ) : null}
                  <Box component="span" sx={visuallyHidden}>
                    {published
                      ? " Published schedule"
                      : estimated
                        ? " Estimated start — may change"
                        : ""}
                  </Box>
                </Typography>
              ) : null}
              {match.dispatch_reason ? (
                <Typography color="text.secondary" variant="body2">
                  {stateLabel(match.dispatch_reason)}
                </Typography>
              ) : null}
              {match.state !== "pending" ? (
                <Typography color="text.secondary" variant="body2">
                  {stateLabel(match.state)}
                </Typography>
              ) : null}
              {[
                "on_court",
                "warmup",
                "in_progress",
                "interval",
                "suspended",
              ].includes(match.state) && score.length > 0 ? (
                <Typography
                  aria-live="polite"
                  color="text.secondary"
                  variant="body2"
                >
                  Live score (your side first):{" "}
                  {score
                    .map((game) => `${game.side1}–${game.side2}`)
                    .join(", ")}
                </Typography>
              ) : null}
              {match.blockers?.map((blocker, blockerIndex) => (
                <Typography
                  key={`${blocker.code}:${blocker.until ?? blockerIndex}`}
                  color="text.secondary"
                  variant="body2"
                >
                  Blocker: {titleLabel(blocker.code)}
                </Typography>
              ))}
              {match.rest_deadline ? (
                <Typography color="text.secondary" variant="body2">
                  Rest until{" "}
                  {new Date(match.rest_deadline).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                    timeZone: match.venue_timezone ?? undefined,
                  })}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        );
      })}
      <TournamentPublicLiveBoard courts={courtBoard} schedule={schedule} />
      {recentResults.length > 0 ? (
        <Stack component="section" aria-label="Recent results" spacing={1}>
          <Typography variant="subtitle1">Recent results</Typography>
          {recentResults.map((match, index) => {
            const score = match.score ?? [];
            return (
              <Paper
                key={`${match.match.kind}:${match.match.match_id}:${index}`}
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {opponentLabel(match)}
                  </Typography>
                  <Typography variant="body2">
                    Result:{" "}
                    {match.result
                      ? `${titleLabel(match.result)} · ${titleLabel(match.outcome ?? match.state)}`
                      : titleLabel(match.outcome ?? match.state)}
                  </Typography>
                  {score.length > 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      Score:{" "}
                      {score
                        .map((game) => `${game.side1}–${game.side2}`)
                        .join(", ")}
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}
